/**
 * Payment hooks.
 *
 * The only module where every mutation carries an idempotency key, and the only
 * one where **nothing is optimistic**. Everywhere else a fast local update is
 * worth a rare correction; here a screen that says "paid" before the server
 * agrees is a screen that sends a guest away without taking their money.
 *
 * Keys are generated once per attempt and reused across retries. A key
 * regenerated on retry is not an idempotency key — it is a second charge with
 * extra steps.
 */

import { useCallback, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import type { ApiError } from './client';
import { formatMoney } from './menu';
import { useApi } from './provider';

export type TenderType = 'cash' | 'card' | 'voucher' | 'house_account';

/**
 * The tenders in the words a till says them.
 *
 * `house_account` is the API's name for the row and reads as a database
 * column on a receipt line. What a waiter and a guest both call it is an
 * account.
 */
export const TENDER_LABELS: Record<TenderType, string> = {
  cash: 'Cash',
  card: 'Card',
  // Said as a guest says them. "Voucher" and "Account" were the ledger's
  // words, and left a new cashier guessing which one a gift card was.
  voucher: 'Gift card',
  house_account: 'On account',
};

export interface PaymentRecord {
  id: string;
  order_id: string;
  tender_type: TenderType;
  /** What this payment took off the bill. */
  amount: string;
  /**
   * What changed hands. Above `amount` only when a guest hands over more than
   * the bill — a note, or a voucher worth more than the meal. A cash line on a
   * receipt should show this and the change, since that is what the guest
   * watched happen.
   */
  tendered: string;
  tip_amount: string;
  state: string;
  captured_at: string;
  refunded_amount: string;
  refundable_amount: string;
}

export interface Bill {
  order_id: string;
  total_due: string;
  paid: string;
  tips: string;
  outstanding: string;
  change: string;
  is_settled: boolean;
  payments: PaymentRecord[];
}

export interface Drawer {
  id: string;
  location_id: string;
  terminal_id: string | null;
  opening_float: string;
  opened_at: string;
  closed_at: string | null;
  cash_in: string;
  cash_out: string;
  expected_total: string;
  counted_total: string | null;
  variance: string | null;
}

export interface Split {
  total: string;
  ways: number;
  shares: string[];
}

export function useBill(orderId: string | null) {
  const api = useApi();
  return useQuery({
    queryKey: ['payments', 'bill', orderId],
    queryFn: ({ signal }) => api.get<Bill>(`/v1/payments/bills/${orderId}`, signal),
    enabled: Boolean(orderId),
    // Money must never be read from a stale cache. Two terminals settling one
    // table is exactly the case where the second needs the truth.
    staleTime: 0,
  });
}

export function useSplit(orderId: string | null, ways: number) {
  const api = useApi();
  return useQuery({
    queryKey: ['payments', 'split', orderId, ways],
    queryFn: ({ signal }) =>
      api.get<Split>(`/v1/payments/bills/${orderId}/split?ways=${ways}`, signal),
    enabled: Boolean(orderId) && ways > 1,
    staleTime: 0,
  });
}

/** One share of a bill, and the lines it stands for. */
export interface SplitPart {
  key: string;
  label: string;
  line_ids: string[];
  amount: string;
}

export interface SplitParts {
  total: string;
  by: 'item' | 'seat';
  parts: SplitPart[];
}

/**
 * The bill divided by what was ordered rather than by how many people.
 *
 * Never cached: each call returns only the lines nobody has paid for yet, so
 * a stale answer is a guest being asked for food somebody else already
 * settled. Professional and above — the query is simply not run below it,
 * because a 402 arriving while a table waits helps nobody.
 */
export function useSplitParts(orderId: string | null, by: 'item' | 'seat' | null) {
  const api = useApi();
  return useQuery({
    queryKey: ['payments', 'split-parts', orderId, by],
    queryFn: ({ signal }) =>
      api.get<SplitParts>(`/v1/payments/bills/${orderId}/split-parts?by=${by}`, signal),
    enabled: Boolean(orderId) && by !== null,
    staleTime: 0,
  });
}

export interface TakePaymentInput {
  orderId: string;
  tender_type: TenderType;
  amount: string;
  tip?: string;
  idempotencyKey: string;
  /**
   * Which registered till is taking this. For cash it decides which drawer
   * the money is counted into: without it the server puts cash in whichever
   * drawer at the site was opened last, so two tills at one site reconcile
   * against each other's takings.
   */
  terminal_id?: string;
  /**
   * The lines this payment is for, when a guest is paying for what they had.
   *
   * Recorded against the payment so the next split leaves them out — which is
   * the whole difference between splitting by item and splitting four ways.
   * Professional and above; sending it below that is refused rather than
   * quietly ignored, because ignoring it would charge somebody twice.
   */
  line_ids?: string[];
  /**
   * Required when `tender_type` is `voucher`: the code from the card.
   *
   * Without it the payment is refused with `field: "voucher_code"`. It has to
   * be — a voucher with no code used to settle a bill of any size from
   * nowhere. Check the balance first with `useGiftCard` so a card short of the
   * bill offers to pay what it can rather than failing at the till.
   */
  voucher_code?: string;
  /**
   * Required when `tender_type` is `house_account`: which account the bill
   * goes on.
   *
   * An id rather than a typed code, because an account is a named customer
   * picked from a list — where a voucher is a bearer instrument and its code
   * is the only thing standing between a stranger and the money. Check
   * `available_credit` before offering it: an account near its ceiling should
   * offer to take part of the bill rather than fail at the till.
   */
  house_account_id?: string;
}

export function useTakePayment() {
  const api = useApi();
  const queryClient = useQueryClient();

  return useMutation<Bill, ApiError, TakePaymentInput>({
    mutationFn: ({ orderId, idempotencyKey, ...body }) =>
      api.post<Bill>(`/v1/payments/bills/${orderId}/payments`, body, { idempotencyKey }),
    onSuccess: (bill) => {
      queryClient.setQueryData(['payments', 'bill', bill.order_id], bill);
      // Both splits are of what is left, so both are wrong the moment any of
      // it is paid. The by-item one is worse than stale: it would still be
      // offering a share somebody has just settled, and taking it would be
      // charging for the same food twice.
      void queryClient.invalidateQueries({ queryKey: ['payments', 'split'] });
      void queryClient.invalidateQueries({ queryKey: ['payments', 'split-parts'] });
      void queryClient.invalidateQueries({ queryKey: ['orders'] });
      // A settled bill closes the tab, which frees the table and clears the
      // kitchen. Refresh both rather than letting them drift until a poll.
      void queryClient.invalidateQueries({ queryKey: ['floor'] });
      void queryClient.invalidateQueries({ queryKey: ['kds'] });
    },
  });
}

export function useAbandonPayment() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<void, ApiError, { orderId: string }>({
    mutationFn: ({ orderId }) => api.post<void>(`/v1/payments/bills/${orderId}/abandon`),
    onSettled: (_d, _e, variables) => {
      void queryClient.invalidateQueries({ queryKey: ['payments', 'bill', variables.orderId] });
      void queryClient.invalidateQueries({ queryKey: ['orders'] });
    },
  });
}

export function useRefund() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    PaymentRecord,
    ApiError,
    { paymentId: string; reason: string; amount?: string; idempotencyKey: string }
  >({
    mutationFn: ({ paymentId, idempotencyKey, ...body }) =>
      api.post<PaymentRecord>(`/v1/payments/payments/${paymentId}/refund`, body, {
        idempotencyKey,
      }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['payments'] });
    },
  });
}

/**
 * The drawer open at a till, or null if there is none.
 *
 * `terminalId` matters. Without it the server answers with the most recently
 * opened drawer anywhere at the site, which is right for a site with one till
 * and wrong the moment there are two: each would show, and take cash into,
 * the other's drawer. Pass it whenever this device knows which till it is.
 */
export function useCurrentDrawer(locationId: string | undefined, terminalId?: string) {
  const api = useApi();
  const search = new URLSearchParams();
  if (locationId) search.set('location_id', locationId);
  if (terminalId) search.set('terminal_id', terminalId);
  return useQuery({
    queryKey: ['payments', 'drawer', locationId ?? null, terminalId ?? null],
    queryFn: ({ signal }) =>
      api.get<Drawer | null>(`/v1/payments/drawers/current?${search.toString()}`, signal),
    enabled: Boolean(locationId),
    staleTime: 10_000,
  });
}

export function useOpenDrawer() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    Drawer,
    ApiError,
    { location_id: string; opening_float: string; terminal_id?: string }
  >({
    mutationFn: (input) => api.post<Drawer>('/v1/payments/drawers', input),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['payments', 'drawer'] });
    },
  });
}

export function useCloseDrawer() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    Drawer,
    ApiError,
    { drawerId: string; counted_total: string; note?: string }
  >({
    mutationFn: ({ drawerId, ...body }) =>
      api.post<Drawer>(`/v1/payments/drawers/${drawerId}/close`, body),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['payments', 'drawer'] });
    },
  });
}

/**
 * One idempotency key per payment attempt, stable across retries.
 *
 * The key must survive a failed request and a user pressing the button again
 * after a timeout — that is the whole scenario it exists for. `reset` is called
 * only once a payment has actually succeeded, which starts a fresh attempt.
 */
export function usePaymentAttempt(): { key: string; reset: () => void } {
  const [key, setKey] = useState(() => crypto.randomUUID());
  const reset = useCallback(() => setKey(crypto.randomUUID()), []);
  return { key, reset };
}

/**
 * A gift card, and what is left on it.
 *
 * `voucher` was an accepted tender with nothing behind it — no card, no
 * balance, no code — so any bill could be settled by naming that tender. The
 * paywall was correct the whole time, which is what made it invisible.
 */
export interface GiftCard {
  id: string;
  /** A bearer instrument. Generated, never chosen. */
  code: string;
  initial_value: string;
  balance: string;
  /** Null means no expiry, which is the default and often the only lawful one. */
  expires_at: string | null;
  /** Cleared when a card is reported lost or stolen. The balance stays. */
  is_active: boolean;
  note: string | null;
}

export interface GiftCardEntry {
  kind: 'issued' | 'redeemed' | 'refunded' | 'topped_up' | 'voided';
  /** Signed against the balance: negative took money off the card. */
  amount: string;
  balance_after: string;
  payment_id: string | null;
  occurred_at: string;
}

/**
 * What is on a card. Reading, not spending.
 *
 * Check this before offering a voucher tender: a card short of the bill should
 * offer to pay what it can, with the rest on another tender, rather than
 * failing at the till with somebody holding the card. A code that does not
 * exist gives the same 404 as one belonging to another restaurant.
 *
 * Never cached. A balance a second out of date is a balance two tills can
 * both spend.
 */
export function useGiftCard(code: string | undefined) {
  const api = useApi();
  // Typed `ApiError` rather than `Error` because the status is the whole
  // answer here: a 404 is "no card with that code", which is a sentence worth
  // writing, and anything else is unexpected and keeps the server's words.
  return useQuery<GiftCard, ApiError>({
    queryKey: ['payments', 'gift-card', code ?? null],
    queryFn: ({ signal }) => api.get<GiftCard>(`/v1/payments/gift-cards/${code}`, signal),
    enabled: Boolean(code),
    staleTime: 0,
    gcTime: 0,
  });
}

/** Every movement on a card, oldest first. */
export function useGiftCardEntries(code: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ['payments', 'gift-card', code ?? null, 'entries'],
    queryFn: ({ signal }) =>
      api.get<GiftCardEntry[]>(`/v1/payments/gift-cards/${code}/entries`, signal),
    enabled: Boolean(code),
    staleTime: 0,
  });
}

/**
 * Sell or give a card. Professional and above.
 *
 * The code comes back in the response and is the only time it is returned as
 * printed — show it, and expect somebody to write it on a card by hand.
 */
export function useIssueGiftCard() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<GiftCard, ApiError, { value: string; expiresAt?: string; note?: string }>({
    mutationFn: (input) =>
      api.post<GiftCard>('/v1/payments/gift-cards', {
        value: input.value,
        expires_at: input.expiresAt ?? null,
        note: input.note ?? null,
      }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['payments', 'gift-card'] });
    },
  });
}

/**
 * Stop a card being spent — lost, stolen, or issued by mistake.
 *
 * Not a delete, and the balance stays on it. Label it "Cancel card" rather
 * than "Delete", and do not use destructive-confirmation language about losing
 * the money: the money is exactly what is kept, so somebody ringing up about
 * the card can be told what it was worth.
 */
export function useCancelGiftCard() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<GiftCard, ApiError, { code: string }>({
    mutationFn: ({ code }) => api.post<GiftCard>(`/v1/payments/gift-cards/${code}/cancel`),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['payments', 'gift-card'] });
    },
  });
}

/**
 * A customer who eats now and is invoiced later.
 *
 * `house_account` was an accepted tender with no account behind it — a bill
 * settled against nothing, the debt recorded nowhere, and the takings showing
 * money received that would never arrive.
 */
export interface HouseAccount {
  id: string;
  /** Short and typed at a till: "ACME", "ROOM12". Not a secret. */
  code: string;
  name: string;
  contact_email: string | null;
  contact_phone: string | null;
  /** Null means no ceiling, which is not the same as a ceiling of zero. */
  credit_limit: string | null;
  /**
   * What the account owes. **Negative means they are in credit** after
   * overpaying an invoice, which is ordinary rather than an error — so do not
   * render this with `Math.abs` or a bare "owes" label.
   */
  balance: string;
  /**
   * What is left before the ceiling stops them. Null when there is no ceiling.
   *
   * Null and "0.00" must read differently: one is unlimited, the other is an
   * account that cannot be charged another penny.
   */
  available_credit: string | null;
  is_active: boolean;
  note: string | null;
}

export interface HouseAccountEntry {
  kind: 'opened' | 'charged' | 'settled' | 'refunded' | 'adjusted';
  /** Signed: positive is money owed, negative is money paid or written off. */
  amount: string;
  balance_after: string;
  payment_id: string | null;
  reference: string | null;
  occurred_at: string;
}

/**
 * The accounts a bill can be charged to. Closed ones are hidden.
 *
 * Readable with the permission to take a payment, because a waiter picks from
 * this at the till. Opening one and setting its ceiling is a different
 * permission and belongs on a back-office screen.
 */
export function useHouseAccounts(includeClosed = false) {
  const api = useApi();
  return useQuery({
    queryKey: ['payments', 'house-accounts', includeClosed],
    queryFn: ({ signal }) =>
      api.get<HouseAccount[]>(
        `/v1/payments/house-accounts?include_closed=${includeClosed}`,
        signal,
      ),
    // A balance two tills can both spend is a balance that is out of date.
    staleTime: 0,
  });
}

/**
 * Every charge and settlement, oldest first. What gets invoiced.
 *
 * A running balance with no history behind it is an invoice nobody can query,
 * so this is the screen somebody actually sends at the end of the month.
 */
export function useHouseAccountStatement(accountId: string | undefined) {
  const api = useApi();
  return useQuery({
    queryKey: ['payments', 'house-accounts', accountId ?? null, 'statement'],
    queryFn: ({ signal }) =>
      api.get<HouseAccountEntry[]>(
        `/v1/payments/house-accounts/${accountId}/statement`,
        signal,
      ),
    enabled: Boolean(accountId),
    staleTime: 0,
  });
}

/**
 * Open an account for a customer trusted to pay later.
 *
 * Leaving `creditLimit` off means no ceiling. Make that a choice on the form
 * rather than something somebody arrives at by not filling a field in — an
 * account with no limit is a customer who can run up any bill at all.
 */
export function useOpenHouseAccount() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    HouseAccount,
    ApiError,
    {
      code: string;
      name: string;
      creditLimit?: string;
      contactEmail?: string;
      contactPhone?: string;
      note?: string;
    }
  >({
    mutationFn: (input) =>
      api.post<HouseAccount>('/v1/payments/house-accounts', {
        code: input.code,
        name: input.name,
        credit_limit: input.creditLimit ?? null,
        contact_email: input.contactEmail ?? null,
        contact_phone: input.contactPhone ?? null,
        note: input.note ?? null,
      }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['payments', 'house-accounts'] });
    },
  });
}

/**
 * Record that the customer paid their invoice.
 *
 * `reference` is a cheque number or a bank reference — whatever lets somebody
 * match this against what actually arrived. Overpaying is allowed and puts the
 * account into credit.
 */
export function useSettleHouseAccount() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<
    HouseAccount,
    ApiError,
    { accountId: string; amount: string; reference?: string }
  >({
    mutationFn: ({ accountId, amount, reference }) =>
      api.post<HouseAccount>(`/v1/payments/house-accounts/${accountId}/settle`, {
        amount,
        reference: reference ?? null,
      }),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['payments', 'house-accounts'] });
    },
  });
}

/**
 * Stop an account being charged. What it owes stays owed.
 *
 * **Closing is not forgiving a debt** and the two must never be one button. Do
 * not word the confirmation as though the balance goes away — a closed account
 * with money outstanding still appears on a statement and still has to be
 * chased.
 */
export function useCloseHouseAccount() {
  const api = useApi();
  const queryClient = useQueryClient();
  return useMutation<HouseAccount, ApiError, { accountId: string }>({
    mutationFn: ({ accountId }) =>
      api.post<HouseAccount>(`/v1/payments/house-accounts/${accountId}/close`),
    onSettled: () => {
      void queryClient.invalidateQueries({ queryKey: ['payments', 'house-accounts'] });
    },
  });
}

/**
 * A card code as it is meant to be read: grouped in fours.
 *
 * The stored and transmitted form is canonical and ungrouped — it is an
 * identifier, and `normalise` on the server exists so a person's punctuation
 * never decides whether their card works. Grouping is presentation, for the
 * same reason card numbers are grouped: an unbroken run of sixteen is
 * transcribed wrong often enough to matter, and somebody is going to read this
 * one aloud across a counter.
 *
 * Regroups rather than inserts, so it is safe on a code that already carries
 * dashes — a formatted code handed back through here comes out unchanged
 * instead of turning into `CA7K--P7U-P2U4-F69A-J`.
 */
export function formatGiftCardCode(code: string): string {
  const bare = code.replace(/[^A-Za-z0-9]/g, '');
  return (bare.match(/.{1,4}/g) ?? [code]).join('-');
}

/**
 * What a movement on a card was, in the words somebody would use about it.
 *
 * `refunded` is money going back *onto* the card — a bill settled by voucher
 * and then refunded — so it reads as a return rather than as a refund to the
 * customer, which is the opposite direction.
 */
export const GIFT_CARD_ENTRY_LABELS: Record<GiftCardEntry['kind'], string> = {
  issued: 'Issued',
  redeemed: 'Spent',
  refunded: 'Put back',
  topped_up: 'Topped up',
  voided: 'Stopped',
};

export const HOUSE_ACCOUNT_ENTRY_LABELS: Record<HouseAccountEntry['kind'], string> = {
  opened: 'Opened',
  charged: 'Charged',
  settled: 'Paid',
  refunded: 'Refunded',
  adjusted: 'Adjusted',
};

/**
 * A house account balance in words: "Owes £120.00", "£30.00 in credit".
 *
 * Stated here rather than in a component because the till and the back office
 * both show it and must not disagree about which direction it runs. A negative
 * balance is a customer who overpaid an invoice — ordinary, not an error — and
 * the absolute value is only ever taken alongside the words that say which way
 * round it is.
 */
export function describeAccountBalance(balance: string): string {
  const value = Number(balance);
  if (!Number.isFinite(value)) return balance;
  if (value === 0) return 'Nothing owed';
  if (value < 0) return `${formatMoney(Math.abs(value).toFixed(2))} in credit`;
  return `Owes ${formatMoney(balance)}`;
}

/**
 * What is left before the ceiling stops them.
 *
 * Three different facts and three different sentences. No ceiling is not a
 * large ceiling, and a ceiling reached is not a ceiling missing — an account
 * showing "—" for both would let a waiter charge one bill and be refused the
 * next with no way to have known.
 */
export function describeAvailableCredit(account: HouseAccount): string {
  if (account.credit_limit === null) return 'No ceiling';
  if (account.available_credit === null) return 'No ceiling';
  if (Number(account.available_credit) <= 0) return 'At its ceiling';
  return `${formatMoney(account.available_credit)} left`;
}

/**
 * A drawer's variance in words: "£2.40 short", "£1.00 over", "Balanced".
 *
 * Counted minus expected, so negative is short. Stated once, here, so the
 * till that closes a drawer and the day report that lists it cannot word the
 * same figure two ways — the report printed "-£2.40" and the till would have
 * said "short", and a manager reading both would reasonably wonder whether
 * they were the same drawer.
 */
export function describeDrawerVariance(variance: string | null): string {
  if (variance === null) return 'Not counted yet';
  const value = Number(variance);
  if (!Number.isFinite(value)) return variance;
  if (value === 0) return 'Balanced';
  const amount = formatMoney(Math.abs(value).toFixed(2));
  return value < 0 ? `${amount} short` : `${amount} over`;
}
